import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import Table from '../../../components/common/agGrid'
import dentistService, { Dentist } from '../../../Services/dentist.services'
import credentialService from '../../../Services/credential.services'
import { clearActingAs, setActingAs } from '../../../helpers/actingAs'
import useAuth from '../../../hooks/useAuth'
import { Roles } from '../../../datasources/graphql/client-types'
import NotFound from '../../notFound/NotFound'
import { Button } from '@material-tailwind/react'
import CreateDentistDialog from './CreateDentistDialog'

const DentistList = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [dentists, setDentists] = useState<Dentist[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  // The back end only honours the act-as header for admins, so practice admins don't get this page
  const isAdmin = user?.role == Roles.Admin || user?.role == '0'

  const getDentists = async () => {
    const result = await dentistService.GetDentists()
    if (!result.success) {
      toast.error('Could not load dentists. Please try again later.', { position: 'top-center' })
    }
    setDentists(result.data)
    setIsLoading(false)
  }

  useEffect(() => {
    if (!isAdmin) {
      return
    }
    getDentists()
  }, [isAdmin])

  if (!isAdmin) {
    return <NotFound />
  }

  const actAsDentist = async (email: string) => {
    setActingAs(email)
    // Runs as the dentist now, so this is the dentist's next step (state selection if they have no request)
    const url = await credentialService.GetNextPage()
    if (typeof url !== 'string') {
      clearActingAs()
      toast.error("Could not open this dentist's account. Please try again.", { position: 'top-center' })
      return
    }
    navigate('/' + url)
  }

  const columns = [
    {
      headerName: 'Name',
      colId: 'name',
      valueGetter: (params: any) => `${params.data.firstName ?? ''} ${params.data.lastName ?? ''}`.trim(),
    },
    { headerName: 'Email', field: 'email' },
    {
      headerName: 'Act as dentist',
      colId: 'actAs',
      sortable: false,
      filter: false,
      cellRenderer: () => (
        <a href="#" role="button">
          Act as dentist
        </a>
      ),
      cellStyle: { color: 'blue', 'text-decoration': 'underline' },
    },
    {
      headerName: 'View credential requests',
      colId: 'viewRequests',
      sortable: false,
      filter: false,
      cellRenderer: () => (
        <a href="#" role="button">
          View credential requests
        </a>
      ),
      cellStyle: { color: 'blue', 'text-decoration': 'underline' },
    },
  ]

  const onDentistCreated = (email: string, startCredentialing: boolean) => {
    setIsCreateOpen(false)
    if (startCredentialing) {
      // A new dentist has no request yet, so their first step is state selection
      setActingAs(email)
      navigate('/credentialing')
      return
    }
    toast.success('Dentist created', { position: 'top-center' })
    getDentists()
  }

  const onCellClicked = (cell: any) => {
    if (cell.column.colId == 'actAs') {
      actAsDentist(cell.data.email)
    } else if (cell.column.colId == 'viewRequests') {
      navigate('/insuranceRequests', { state: { search: cell.data.email } })
    }
  }

  return (
    <div>
      <div className="mx-auto px-4 sm:px-8 flex-grow">
        <div className="py-5">
          <div className="flex flex-row mb-1 sm:mb-0 justify-between w-full">
            <h1 className="text-2xl leading-tight text-3xl">Dentists</h1>
            <Button onClick={() => setIsCreateOpen(true)}>Create dentist</Button>
          </div>
          <p>Find a dentist to work on their credentialing for them.</p>
        </div>
      </div>
      {isLoading ? (
        <p>Loading...</p>
      ) : (
        <Table rowData={dentists} columns={columns} getOnclickCellData={onCellClicked} />
      )}
      <CreateDentistDialog isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} onCreated={onDentistCreated} />
    </div>
  )
}

export default DentistList
